import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { format } from 'date-fns';
import { PageHeader } from '@/components/shared/PageHeader';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { RefreshButton } from '@/components/shared/RefreshButton';
import { ErrorState } from '@/components/shared/ErrorState';
import { FinancialMetricsCards } from '@/components/features/financial/FinancialMetricsCards';
import { FinancialChart } from '@/components/features/financial/FinancialChart';
import { IncomeVsExpensesChart } from '@/components/features/financial/IncomeVsExpensesChart';
import { FinancialTotalsCard } from '@/components/features/financial/FinancialTotalsCard';
import { FinancialFilters } from '@/components/features/financial/FinancialFilters';
import {
  useFinancialMetrics,
  useFinancialIncome,
  useFinancialExpenses,
  useFinancialProfit,
  useFinancialTotals,
} from '@/lib/hooks/useFinancial';
import type { PeriodType } from '@/lib/types';
import { ROUTES } from '@/config/routes';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { exportFinancialDataToCSV } from '@/utils/export';

export const Route = createFileRoute('/_auth/_admin/admin/financial/')({
  component: FinancialPage,
});

function FinancialPage() {
  const [period, setPeriod] = useState<PeriodType>('day');
  const [fromDate, setFromDate] = useState<Date>();
  const [toDate, setToDate] = useState<Date>();

  const params = {
    period,
    from_date: fromDate ? format(fromDate, 'yyyy-MM-dd') : undefined,
    to_date: toDate ? format(toDate, 'yyyy-MM-dd') : undefined,
  };

  const {
    data: metrics,
    isLoading: metricsLoading,
    isError: metricsError,
    refetch: refetchMetrics,
  } = useFinancialMetrics(params);

  const {
    data: income,
    isLoading: incomeLoading,
    refetch: refetchIncome,
  } = useFinancialIncome(params);

  const {
    data: expenses,
    isLoading: expensesLoading,
    refetch: refetchExpenses,
  } = useFinancialExpenses(params);

  const {
    data: profit,
    isLoading: profitLoading,
    refetch: refetchProfit,
  } = useFinancialProfit(params);

  const {
    data: totals,
    isLoading: totalsLoading,
    refetch: refetchTotals,
  } = useFinancialTotals();

  const isLoading = metricsLoading || incomeLoading || expensesLoading || profitLoading || totalsLoading;

  const handleRefresh = () => {
    refetchMetrics();
    refetchIncome();
    refetchExpenses();
    refetchProfit();
    refetchTotals();
  };

  const handleClearFilters = () => {
    setFromDate(undefined);
    setToDate(undefined);
  };

  const breadcrumbs = [
    { label: 'Админ', href: ROUTES.ADMIN.ROOT },
    { label: 'Финансы' },
  ];

  if (metricsError) {
    return (
      <div className="space-y-6">
        <PageHeader title="Финансы" />
        <Breadcrumbs items={breadcrumbs} />
        <ErrorState
          title="Failed to load financial data"
          message="There was an error loading the financial statistics."
          onRetry={handleRefresh}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Финансы"
        description="Отслеживание доходов, расходов и прибыльности"
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                if (income && expenses && profit) {
                  exportFinancialDataToCSV(income, expenses, profit);
                }
              }}
              disabled={!income || !expenses || !profit}
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <RefreshButton onClick={handleRefresh} isLoading={isLoading} />
          </div>
        }
      />
      <Breadcrumbs items={breadcrumbs} />

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
            Доход представляет собой комиссию за транзакции. Расходы включают в себя оплату за электроэнергию (0,345 TRX) и стоимость полосы пропускания при каждом выводе средств и внутреннем переводе.
        </AlertDescription>
      </Alert>

      <FinancialFilters
        period={period}
        fromDate={fromDate}
        toDate={toDate}
        onPeriodChange={setPeriod}
        onFromDateChange={setFromDate}
        onToDateChange={setToDate}
        onClear={handleClearFilters}
      />

      <FinancialMetricsCards metrics={metrics} isLoading={metricsLoading} />

      <div className="grid gap-6 md:grid-cols-2">
        <FinancialTotalsCard totals={totals} isLoading={totalsLoading} />
        <FinancialChart
          title="Профит за всё время"
          data={profit}
          isLoading={profitLoading}
          dataKey="profit"
          color="#10b981"
        />
      </div>

      <IncomeVsExpensesChart
        incomeData={income}
        expensesData={expenses}
        isLoading={incomeLoading || expensesLoading}
      />

      <div className="grid gap-6 md:grid-cols-2">
        <FinancialChart
          title="Income Over Time"
          data={income}
          isLoading={incomeLoading}
          dataKey="total"
          color="#10b981"
        />
        <FinancialChart
          title="Expenses Over Time"
          data={expenses}
          isLoading={expensesLoading}
          dataKey="total"
          color="#ef4444"
        />
      </div>
    </div>
  );
}
